import { useState, useRef, useEffect } from 'react';

export function useRoundOverlays() {
  // Video round state
  const [videoRoundState, setVideoRoundState] = useState(null);
  const [videoRoundContainerKey, setVideoRoundContainerKey] = useState(null);
  const [videoRoundBlob, setVideoRoundBlob] = useState(null);
  const [videoRoundUrl, setVideoRoundUrl] = useState(null);
  const [videoRoundPosition, setVideoRoundPosition] = useState({ x: 100, y: 100 });
  const videoRoundStreamRef = useRef(null);
  const videoRoundRecorderRef = useRef(null);
  const videoRoundChunksRef = useRef([]);
  const videoRoundDragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0, insertedId: null });
  const videoRoundDidDragRef = useRef(false);
  const videoRoundVideoRef = useRef(null);
  const videoRoundPreviewRef = useRef(null);
  const [videoRoundProgress, setVideoRoundProgress] = useState({ currentTime: 0, duration: 1 });
  const [videoRoundPlaying, setVideoRoundPlaying] = useState(false);
  const [insertedRoundVideos, setInsertedRoundVideos] = useState([]);
  const [insertedRoundHoveredId, setInsertedRoundHoveredId] = useState(null);
  const [videoRoundOverlayHovered, setVideoRoundOverlayHovered] = useState(false);

  // Audio round state
  const [audioRoundState, setAudioRoundState] = useState(null);
  const [audioRoundContainerKey, setAudioRoundContainerKey] = useState(null);
  const [audioRoundBlob, setAudioRoundBlob] = useState(null);
  const [audioRoundUrl, setAudioRoundUrl] = useState(null);
  const [audioRoundPosition, setAudioRoundPosition] = useState({ x: 100, y: 100 });
  const audioRoundStreamRef = useRef(null);
  const audioRoundRecorderRef = useRef(null);
  const audioRoundChunksRef = useRef([]);
  const audioRoundDragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0, insertedId: null });
  const audioRoundDidDragRef = useRef(false);
  const audioRoundAudioRef = useRef(null);
  const [audioRoundProgress, setAudioRoundProgress] = useState({ currentTime: 0, duration: 1 });
  const [audioRoundPlaying, setAudioRoundPlaying] = useState(false);
  const [audioRoundOverlayHovered, setAudioRoundOverlayHovered] = useState(false);
  const [insertedRoundAudios, setInsertedRoundAudios] = useState([]);
  const [insertedRoundAudiosHoveredId, setInsertedRoundAudiosHoveredId] = useState(null);

  // Text round state
  const [textRoundState, setTextRoundState] = useState(null);
  const [textRoundDraft, setTextRoundDraft] = useState('');
  const [textRoundPosition, setTextRoundPosition] = useState({ x: 150, y: 150 });
  const [textRoundContainerKey, setTextRoundContainerKey] = useState(null);
  const textRoundDragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0, insertedId: null });
  const [insertedRoundTexts, setInsertedRoundTexts] = useState([]);

  // Video: подставляем поток в video при записи
  useEffect(() => {
    if (videoRoundState === 'recording' && videoRoundStreamRef.current && videoRoundVideoRef.current) {
      videoRoundVideoRef.current.srcObject = videoRoundStreamRef.current;
    }
  }, [videoRoundState]);

  // Revoke object URL при размонтировании или сбросе
  useEffect(() => {
    return () => { if (videoRoundUrl) URL.revokeObjectURL(videoRoundUrl); };
  }, [videoRoundUrl]);

  useEffect(() => {
    return () => { if (audioRoundUrl) URL.revokeObjectURL(audioRoundUrl); };
  }, [audioRoundUrl]);

  // Аудио: прогресс и play state превью
  useEffect(() => {
    const el = audioRoundAudioRef.current;
    if (audioRoundState !== 'preview' || !el) return;
    const onTimeUpdate = () => setAudioRoundProgress({ currentTime: el.currentTime, duration: el.duration || 1 });
    const onLoadedMetadata = () => setAudioRoundProgress({ currentTime: 0, duration: el.duration || 1 });
    const onPlay = () => setAudioRoundPlaying(true);
    const onPause = () => setAudioRoundPlaying(false);
    const onEnded = () => setAudioRoundPlaying(false);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    setAudioRoundProgress({ currentTime: 0, duration: el.duration || 1 });
    setAudioRoundPlaying(false);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [audioRoundState, audioRoundUrl]);

  // Видео: прогресс и play state превью
  useEffect(() => {
    const el = videoRoundPreviewRef.current;
    if (videoRoundState !== 'preview' || !el) return;
    const onTimeUpdate = () => setVideoRoundProgress({ currentTime: el.currentTime, duration: el.duration || 1 });
    const onLoadedMetadata = () => setVideoRoundProgress({ currentTime: 0, duration: el.duration || 1 });
    const onPlay = () => setVideoRoundPlaying(true);
    const onPause = () => setVideoRoundPlaying(false);
    const onEnded = () => setVideoRoundPlaying(false);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    setVideoRoundProgress({ currentTime: 0, duration: el.duration || 1 });
    setVideoRoundPlaying(false);
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, [videoRoundState, videoRoundUrl]);

  // Перетаскивание всех трёх оверлеев (один обработчик для оптимизации)
  useEffect(() => {
    const handleMove = (clientX, clientY) => {
      const v = videoRoundDragRef.current;
      const a = audioRoundDragRef.current;
      if (v.isDragging) {
        videoRoundDidDragRef.current = true;
        const dx = v.startLeft + (clientX - v.startX);
        const dy = v.startTop + (clientY - v.startY);
        if (v.insertedId) {
          setInsertedRoundVideos(prev => prev.map(it => it.id === v.insertedId ? { ...it, position: { x: dx, y: dy } } : it));
        } else if (videoRoundState === 'preview') {
          setVideoRoundPosition({ x: dx, y: dy });
        }
      }
      if (a.isDragging) {
        audioRoundDidDragRef.current = true;
        const dx = a.startLeft + (clientX - a.startX);
        const dy = a.startTop + (clientY - a.startY);
        if (a.insertedId) {
          setInsertedRoundAudios(prev => prev.map(it => it.id === a.insertedId ? { ...it, position: { x: dx, y: dy } } : it));
        } else if (audioRoundState === 'preview') {
          setAudioRoundPosition({ x: dx, y: dy });
        }
      }
      const t = textRoundDragRef.current;
      if (t.isDragging) {
        const dx = t.startLeft + (clientX - t.startX);
        const dy = t.startTop + (clientY - t.startY);
        if (t.insertedId) {
          setInsertedRoundTexts(prev => prev.map(it => it.id === t.insertedId ? { ...it, position: { x: dx, y: dy } } : it));
        } else if (textRoundState === 'preview') {
          setTextRoundPosition({ x: dx, y: dy });
        }
      }
    };
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onMouseUp = () => {
      videoRoundDragRef.current.isDragging = false;
      videoRoundDragRef.current.insertedId = null;
      audioRoundDragRef.current.isDragging = false;
      audioRoundDragRef.current.insertedId = null;
      textRoundDragRef.current.isDragging = false;
      textRoundDragRef.current.insertedId = null;
      setTimeout(() => { videoRoundDidDragRef.current = false; audioRoundDidDragRef.current = false; }, 0);
    };
    const onTouchMove = (e) => { if (e.touches[0]) handleMove(e.touches[0].clientX, e.touches[0].clientY); };
    const onTouchEnd = () => {
      videoRoundDragRef.current.isDragging = false;
      videoRoundDragRef.current.insertedId = null;
      audioRoundDragRef.current.isDragging = false;
      audioRoundDragRef.current.insertedId = null;
      textRoundDragRef.current.isDragging = false;
      textRoundDragRef.current.insertedId = null;
      setTimeout(() => { videoRoundDidDragRef.current = false; audioRoundDidDragRef.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [videoRoundState, audioRoundState, textRoundState, insertedRoundVideos.length, insertedRoundAudios.length, insertedRoundTexts.length]);

  return {
    // Video round
    videoRoundState, setVideoRoundState,
    videoRoundContainerKey, setVideoRoundContainerKey,
    videoRoundBlob, setVideoRoundBlob,
    videoRoundUrl, setVideoRoundUrl,
    videoRoundPosition, setVideoRoundPosition,
    videoRoundStreamRef, videoRoundRecorderRef, videoRoundChunksRef,
    videoRoundDragRef, videoRoundDidDragRef,
    videoRoundVideoRef, videoRoundPreviewRef,
    videoRoundProgress, setVideoRoundProgress,
    videoRoundPlaying, setVideoRoundPlaying,
    insertedRoundVideos, setInsertedRoundVideos,
    insertedRoundHoveredId, setInsertedRoundHoveredId,
    videoRoundOverlayHovered, setVideoRoundOverlayHovered,
    // Audio round
    audioRoundState, setAudioRoundState,
    audioRoundContainerKey, setAudioRoundContainerKey,
    audioRoundBlob, setAudioRoundBlob,
    audioRoundUrl, setAudioRoundUrl,
    audioRoundPosition, setAudioRoundPosition,
    audioRoundStreamRef, audioRoundRecorderRef, audioRoundChunksRef,
    audioRoundDragRef, audioRoundDidDragRef,
    audioRoundAudioRef,
    audioRoundProgress, setAudioRoundProgress,
    audioRoundPlaying, setAudioRoundPlaying,
    audioRoundOverlayHovered, setAudioRoundOverlayHovered,
    insertedRoundAudios, setInsertedRoundAudios,
    insertedRoundAudiosHoveredId, setInsertedRoundAudiosHoveredId,
    // Text round
    textRoundState, setTextRoundState,
    textRoundDraft, setTextRoundDraft,
    textRoundPosition, setTextRoundPosition,
    textRoundContainerKey, setTextRoundContainerKey,
    textRoundDragRef,
    insertedRoundTexts, setInsertedRoundTexts,
  };
}
